import React, { useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import moment from "moment";
import { Box, Button, CircularProgress, Grid, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import DeleteIcon from "@mui/icons-material/Delete";
import PropTypes from "prop-types";

import BreadCrumb from "Components/Common/BreadCrumb";
import InputFieldText from "Components/Common/InputFieldText";
import BaseTable from "Components/Common/BaseTable";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import CreditNoteForm from "./CreditNoteForm";
import Tooltip from "Components/Common/Tooltip";
import ConfirmationDialog from "Components/Common/ConfirmationDialog";
import DatePicker from "Components/Common/DatePicker";
import { CONSUMER, otherUtilitiesFinanceAdminRoles } from "common/constant";
import { CREDIT_NOTE } from "tagTypes";

import { formatUtilityAddress } from "utils/formatUtilityAddress";
import debounceFn from "utils/debounce";
import { getImageUrl, isAuthorizedUser } from "utils";

import {
  useDeleteCreditNoteMutation,
  usePostSendDebitNoticeMutation,
  usePutCreditNoteDetailsMutation,
} from "apis/UtilityConsumer/utilityConsumer";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import { getOptionLabel } from "common/enums";

const CreditNote = ({ t }) => {
  const [sendDebitNotice, { isLoading: isDebitNoticeSending }] =
    usePostSendDebitNoticeMutation();

  const [deleteCreditNote, { isLoading: isDeletingData }] =
    useDeleteCreditNoteMutation();

  const [approveCreditNote, { isLoading: isCreditNoteLoading }] =
    usePutCreditNoteDetailsMutation();

  const { roles } = useSelector((state) => state.Login);
  const isAdmin = useMemo(() => !roles.includes(CONSUMER), [roles]);

  const [documentToPreview, setDocumentToPreview] = useState(null);
  const [isCreditNoteApproved, setIsCreditNoteApproved] = useState(null);
  const [consumerNumber, setConsumerNumber] = useState("");
  const [query, setQuery] = useState({
    consumerNumber: "",
    searchDate: isAdmin ? moment() : null,
  });
  const [debitNoteIdForSendNotice, setDebitNoteIdForSendNotice] = useState("");
  const [editModalProps, setEditModalProps] = useState(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [deleteCreditNoteValue, setDeleteCreditNoteValue] = useState("");

  const sendNoticeHandler = async () => {
    try {
      await sendDebitNotice(debitNoteIdForSendNotice);
      setDebitNoteIdForSendNotice("");
    } catch (error) {}
  };

  const columns = useMemo(
    () => [
      {
        title: "Consumer Number",
        field: "consumerNumber",
      },
      {
        title: "Credit Note Number",
        field: "creditNoteNumber",
      },
      {
        title: "Consumer Name",
        field: "consumerName",
      },
      {
        title: "Tariff",
        field: "tariff",
      },
      {
        title: "Subcription Type",
        renderCell: ({ subscriptionType }) =>
          getOptionLabel("subscriptionTypes", subscriptionType) || "--",
      },
      {
        title: "Demand Charges",
        field: "demandCharges",
      },
      {
        title: "Energy Charges",
        field: "energyCharges",
      },
      {
        title: "Fuel Charges",
        field: "fuelCharges",
      },
      {
        title: "Meter Charges",
        field: "meterCharges",
      },
      {
        title: "Adjustments Amount",
        field: "adjustmentAmount",
      },
      {
        title: "Utility Address",
        renderCell: ({ utilityAddress }) => {
          const address = formatUtilityAddress(utilityAddress);

          if (address) {
            return (
              <>
                <p className="m-0">{address}, Gandhinagar-382050</p>
              </>
            );
          }

          return "--";
        },
        columnProps: { style: { width: "350px", minWidth: "350px" } },
      },
      {
        title: "Credit Note Date",
        renderCell: ({ creditNoteDate }) =>
          convertUTCDateToLocalDate(creditNoteDate).format("DD-MM-YYYY"),
      },
      {
        title: "Actions",
        renderCell: ({ url, id, utilityConsumerId, isApproved }) => (
          <Box sx={{ minWidth: "120px" }}>
            <IconButton
              onClick={() => {
                setDocumentToPreview({
                  url: getImageUrl(url),
                  documentTypeName: "Credit Note Document",
                  documentType: "pdf",
                  documentName: "Credit Note Document",
                });
                setIsCreditNoteApproved({ id: id, isApprove: isApproved });
              }}
            >
              <Tooltip title={t("View Credit Note Document")}>
                <RemoveRedEyeIcon fontSize="small" color="primary" />
              </Tooltip>
            </IconButton>

            {isAdmin && (
              <IconButton
                onClick={() => setEditModalProps({ utilityConsumerId, id })}
                disabled={isApproved}
              >
                <Tooltip title={t("Edit Credit Note Details")}>
                  <EditIcon
                    fontSize="small"
                    color={!isApproved ? "primary" : "disabled"}
                  />
                </Tooltip>
              </IconButton>
            )}

            {isAdmin && (
              <IconButton
                onClick={() => {
                  setDeleteCreditNoteValue(id);
                }}
              >
                <Tooltip title={t("Delete Credit Note Details")}>
                  <DeleteIcon fontSize="small" color="primary" />
                </Tooltip>
              </IconButton>
            )}
          </Box>
        ),
      },
    ],
    []
  );

  const approveCreditNoteBtnHandler = async (creditNoteId) => {
    try {
      await approveCreditNote(creditNoteId);
      setDocumentToPreview(null);
      setIsCreditNoteApproved(null);
    } catch (error) {}
  };

  const serverSidePaginationConfig = useMemo(
    () => ({
      endpoint: "/credit-note",
      tags: [CREDIT_NOTE],
      query: {
        ...query,
        searchDate: query.searchDate
          ? encodeURIComponent(query.searchDate.format("YYYY-MM-DD"))
          : "",
      },
    }),
    [query, isAdmin]
  );

  return (
    <>
      {!!debitNoteIdForSendNotice && (
        <ConfirmationDialog
          message="Are you sure you want to send notice?"
          onCancel={() => setDebitNoteIdForSendNotice("")}
          onConfirm={sendNoticeHandler}
          isOpen
          title="Warning"
          variant="warning"
          isLoading={isDebitNoticeSending}
        />
      )}

      {!!deleteCreditNoteValue && (
        <ConfirmationDialog
          message="Are you sure you want to delete?"
          onCancel={() => setDeleteCreditNoteValue("")}
          onConfirm={async () => {
            await deleteCreditNote(deleteCreditNoteValue);
            setDeleteCreditNoteValue("");
          }}
          isOpen={!!deleteCreditNoteValue}
          title="Warning"
          variant="warning"
          isLoading={isDeletingData}
        />
      )}

      <BreadCrumb
        title={t("Credit Note Advice")}
        pageTitle={t(isAdmin ? "Payment" : "")}
      />

      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => {
            setDocumentToPreview(null);
            setIsCreditNoteApproved(null);
          }}
          {...(isAuthorizedUser(roles, otherUtilitiesFinanceAdminRoles) &&
            !isCreditNoteApproved.isApprove && {
              actionButton: (
                <Button
                  color="success"
                  onClick={() =>
                    approveCreditNoteBtnHandler(isCreditNoteApproved.id)
                  }
                  variant="contained"
                >
                  {t("Approve")}
                  {isCreditNoteLoading && <CircularProgress size={20} />}
                </Button>
              ),
            })}
          {...documentToPreview}
        />
      )}

      {(!!editModalProps || isFormModalOpen) && (
        <CreditNoteForm
          editModalProps={editModalProps}
          handleClose={() => {
            setEditModalProps(null);
            setIsFormModalOpen(false);
          }}
        />
      )}

      <Grid container columnSpacing={2} className="d-flex  flex-wrap">
        <Grid item xs={12} sm={6} md={4}>
          <DatePicker
            slotProps={{
              textField: {
                size: "small",
              },
            }}
            label={t("Select Month and Year")}
            className="w-100"
            views={["month", "year"]}
            value={query.searchDate}
            disableFuture
            onChange={(date) =>
              setQuery((prevState) => ({
                ...prevState,
                searchDate: date,
              }))
            }
          />
        </Grid>
        {isAdmin && (
          <>
            <Grid item xs={12} sm={6} md={4}>
              <InputFieldText
                label={t("Consumer Number")}
                placeholder="Enter Consumer Number"
                value={consumerNumber}
                onChange={(e) => {
                  setConsumerNumber(e.target.value);
                  debounceFn(
                    () =>
                      setQuery((prevState) => ({
                        ...prevState,
                        consumerNumber: e.target.value,
                      })),
                    300
                  );
                }}
              />
            </Grid>

            <Grid item>
              <Button
                variant="contained"
                component="label"
                className="mb-2 me-2"
                sx={{ height: "40px", fontSize: "12px" }}
                onClick={() => setIsFormModalOpen(true)}
              >
                {t("Add")}
              </Button>
            </Grid>
          </>
        )}
      </Grid>

      <BaseTable
        columns={columns}
        pageSize={10}
        paginationType="serverSide"
        serverSidePaginationConfig={serverSidePaginationConfig}
      />
    </>
  );
};

CreditNote.propTypes = { t: PropTypes.func };

export default withTranslation()(CreditNote);
